import { useEffect, useState } from 'react';
import {
  IOS_INSTRUCTIONS,
  PlatformInfo,
  captureBeforeInstallPrompt,
  detectPlatform,
  markInstallPromptDismissed,
  shouldShowInstallPrompt,
  triggerNativeInstallPrompt,
} from '../lib/pwa';

/**
 * One-shot install prompt.
 *
 * - iOS : shows a share-sheet instruction panel (Safari has no
 *   beforeinstallprompt event — the user must "Add to Home Screen"
 *   manually).
 * - Android / Desktop Chrome / Samsung : captures
 *   beforeinstallprompt and exposes an Install button that calls it.
 *
 * Dismissal is stored in localStorage with a 7-day cooldown.
 */
export default function InstallPrompt() {
  const [info, setInfo] = useState<PlatformInfo | null>(null);
  const [visible, setVisible] = useState(false);
  const [promptAvailable, setPromptAvailable] = useState(false);

  useEffect(() => {
    const detected = detectPlatform(navigator.userAgent);
    setInfo(detected);
    setVisible(shouldShowInstallPrompt(detected));

    const cleanup = captureBeforeInstallPrompt();
    const onBeforeInstall = () => setPromptAvailable(true);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => setVisible(false);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      cleanup();
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!visible || !info || info.isStandalone) return null;

  const dismiss = () => {
    markInstallPromptDismissed();
    setVisible(false);
  };

  const install = async () => {
    const outcome = await triggerNativeInstallPrompt();
    if (outcome === 'accepted' || outcome === 'dismissed') {
      markInstallPromptDismissed();
      setVisible(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-prompt-title"
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4"
    >
      <div className="w-full max-w-[390px] rounded-2xl border border-outline-variant/25 bg-surface-container shadow-2xl">
        <div className="flex items-start gap-3 px-5 pt-5">
          <span className="material-symbols-outlined text-primary-container" aria-hidden="true">
            add_to_home_screen
          </span>
          <div className="flex-1">
            <h2 id="install-prompt-title" className="text-base font-bold text-primary">
              Install TapNpay on this device
            </h2>
            {/* Phase G WCAG AA contrast fix (axe-core color-contrast):
                text-on-surface-variant (#6B7280) on bg-surface-container
                (#F5F5F5) was 4.43:1, just under the 4.5:1 AA threshold
                at 12px. Darkening to text-on-surface fixes it. */}
            <p className="mt-1 text-xs text-on-surface">
              Faster checkout, saved cart, Apple Pay / Google Pay. No app store.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="rounded-md p-1 text-on-surface transition hover:bg-surface-container-high"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {info.platform === 'ios' && (
          <ol className="mt-3 space-y-2 px-5 pb-5 text-xs text-on-surface">
            {IOS_INSTRUCTIONS.map((step, i) => (
              <li key={i} className="flex gap-2">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-primary-container text-[10px] font-bold text-on-primary-container">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        )}

        {info.canInstallProgrammatically && (
          <div className="flex gap-2 px-5 pb-5">
            <button
              type="button"
              onClick={install}
              disabled={!promptAvailable}
              className="flex-1 rounded-xl bg-primary-container py-3 text-sm font-bold uppercase tracking-wide text-on-primary-container transition disabled:opacity-50"
            >
              {promptAvailable ? 'Install' : 'Preparing…'}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-xl border border-outline-variant/30 px-4 py-3 text-sm text-on-surface"
            >
              Later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
